<template>
	<div class="worksheets-subview">
		<div class="subviewOpts">
			<it-link to="createWorkSheet.do" :ctx="WORKSHEET_REFS" class="button-link button-link-cicon"><sym-icon name="worksheet"></sym-icon> {{ env.msg('menu.addWorkSheet') }}</it-link>
		</div>

		<div id="worksheetsSubview">
			<crm-table :rest-api="restApi" :sum-rest-api="sumRestApi"></crm-table>
		</div>

		<div v-if="sum && sum.count > 0" class="scroll-frame" style="margin-top: 16px">
			<div class="scroll-frame-body">
				<table class="agenda">
					<thead>
						<tr>
							<th>{{ env.msg('worksheet.crmuser') }}</th>
							<th v-for="op in usedOperations" :key="op.id">{{ op.name }}</th>
							<th colspan="2"><sym-icon name="sum"></sym-icon></th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="u in usedUsers" :key="u.id">
							<td>{{ u.name }}</td>
							<td v-for="op in usedOperations" :key="op.id">
								{{ formatedMins(minutesOpUser(u.id, op.id)) }}
							</td>
							<td>{{ formatedMins(minutesUser(u.id)) }}</td>
							<td style="opacity:0.5">{{ formatPerc(minutesUser(u.id) / sum.totalMinutes) }}</td>
						</tr>
					</tbody>
					<tfoot>
						<tr>
							<td class="summary" style="vertical-align: baseline"><sym-icon name="sum"></sym-icon></td>
							<td v-for="op in usedOperations" :key="op.id">
								<div>{{ formatedMins(minutesOp(op.id)) }}</div>
								<div style="opacity:0.5">{{ formatPerc(minutesOp(op.id) / sum.totalMinutes) }}</div>
							</td>
							<td colspan="2" style="vertical-align: baseline"><b>{{ formatedMins(sum.totalMinutes) }}</b></td>
						</tr>
					</tfoot>
				</table>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {ref, computed, watch, onMounted} from "vue";
import ItLink from "@/router/ItLink.vue";
import {injectEnv} from "@fd/Environment.ts";
import SymIcon from "@fd/helpers/SymIcon.vue";
import {ContextKey, getIntValue, injectContext, WORKSHEET_REFS} from "@t/fd/AppContext.ts";
import {loadSourceType, SourceType} from "@t/meta/SourceType.ts";
import CrmTable from "@/vue/table/CrmTable.vue";
import {WorksheetSummaryWithOps} from "@t/worksheet/WorksheetSummary.ts";
import {Operation} from "@t/service/Operation.ts";
import {SimplifiedUser} from "@t/user/SimplifiedUser.ts";
import {formatMinutes, formatPerc} from "@/utils/FormatUtil.ts";

const props = defineProps<{}>();
const env = injectEnv();
const ctx = injectContext();
const sourceType = computed<number>(() => getIntValue(ctx.value, ContextKey.SRC_TYPE));
const sourceId = computed<string|number>(() => getIntValue(ctx.value, ContextKey.SRC_ID));
const type = ref<SourceType>(null);
const sum = ref<WorksheetSummaryWithOps>(null);

watch(sourceType, async () => {
	if (!sourceType.value) return;
	if (type.value == null || type.value.id !== sourceType.value) {
		type.value = await loadSourceType(env, sourceType.value);
	}
}, { immediate: true });

watch([sourceType, sourceId], async () => {
	if (!sourceType.value || !sourceId.value) return;
	let ep = "api/worksheets/overview";
	ep = env.net.urlParam(ep, "operations", "true");
	ep = env.net.urlParam(ep, "srcType", sourceType.value);
	ep = env.net.urlParam(ep, "srcId", sourceId.value);
	sum.value = await env.net.fetch(ep).then((resp)=> {
		if (resp.ok) return resp.json();
		throw new Error("Failed to fetch worksheet summary");
	});
}, { immediate: true });

const restApi = computed<string>(() => {
	let ep = "api/worksheets/agendaSubview";
	ep = env.net.urlParam(ep, "srcType", sourceType.value);
	ep = env.net.urlParam(ep, "srcId", sourceId.value);
	return ep;
});

const sumRestApi = computed<string>(() => {
	let ep = "api/worksheets/agendaSubviewSummary";
	ep = env.net.urlParam(ep, "srcType", sourceType.value);
	ep = env.net.urlParam(ep, "srcId", sourceId.value);
	return ep;
});

/**
 * List of operations which are used in summary
 */
const usedOperations = computed<Operation[]>(() => {
	const used:Operation[] = [];
	if (!sum.value) return used;
	for (let op of sum.value.operations) {
		if (sum.value.minutesPerOperation[op.id]) used.push(op);
	}
	return used;
});

const allUsers = ref<SimplifiedUser[]>([]);
onMounted(() => {
	env.users().then((users) => { allUsers.value = users; });
})

const usedUsers = computed<SimplifiedUser[]>(() => {
	const users:SimplifiedUser[] = [];
	if (!sum.value) return users;
	for (let u of allUsers.value) {
		if (sum.value.minutesPerUser[u.id.toString()]) users.push(u);
	}
	return users;
});

function minutesOpUser(uid:number, opid:string):number {
	const pk = opid + "_" + uid;
	const mins = sum.value.minutes[pk];
	return typeof mins === "number" ? mins : 0;
}

function minutesUser(uid:number):number {
	const userTotal = sum.value.minutesPerUser[uid.toString()];
	return typeof userTotal === "number" ? userTotal : 0;
}

function minutesOp(opid:string):number {
	const opTotal = sum.value.minutesPerOperation[opid];
	return typeof opTotal === "number" ? opTotal : 0;
}

function formatedMins(mins:number):string {
	return mins <= 0 ? "" : formatMinutes(mins);
}

</script>

<style>

</style>